"use client";
import CompressedImage from "@/components/UI/CompressedImage/CompressedImage";
import FormattedPrice from "@/components/UI/FormattedPrice/FormattedPrice";
import useStoreKeys from "@/hooks/useStoreKeys";
import { CartPhysicalItem, ProductData } from "@/lib/5874/types";
import Link from "next/link";
import styles from "./MiniCartItem.module.scss";
import { RemoveItemForm } from "../../Forms/RemoveItemForm/RemoveItemForm";
import { useEffect, useMemo, useState } from "react";
import { getProduct } from "@/lib/bigcommerce/product";
import mapProductOptions from "@/hooks/mapProductOptions";

interface MiniCartItemProps {
	item: CartPhysicalItem;
	handleCartModalClose: () => void;
}

const MiniCartItem = ({ item, handleCartModalClose }: MiniCartItemProps) => {
	const { store, lang } = useStoreKeys();
	const { name, sku, image_url, sale_price, list_price, original_price } = item;
	const url = new URL(item.url);
	const path = url.toString().replace(url.origin, "");
	const href = `/${store}/${lang}${path}`;

	const [product, setProduct] = useState<ProductData | undefined>();

	useEffect(() => {
		getProduct(Number(item.product_id)).then((res) => {
			setProduct(res ?? undefined);
		});
	}, []);

	const productSpecifications = useMemo(() => {
		if (!product) return [];
		return mapProductOptions([product]);
	}, [product]);

	let salePercentage: number | undefined;

	if (sale_price !== original_price) {
		const absDifference = sale_price - original_price;
		const average = (sale_price + original_price) / 2;
		salePercentage = (absDifference / average) * 100;
	}

	const totalPrice =
		(salePercentage !== undefined ? sale_price : list_price) * item.quantity;

	return (
		<div className={styles.item}>
			<CompressedImage
				src={image_url}
				height={64}
				width={64}
				altText={name}
				imgLink={href}
				onClick={() => handleCartModalClose()}
				containerClass={styles.image}
				withNextImage={false}
			/>
			<div className={styles.details}>
				<Link
					className={styles.name}
					href={href}
					onClick={() => handleCartModalClose()}
				>
					{item.quantity} x {name}
				</Link>
				<div className={styles.sku}>{sku}</div>
				{productSpecifications && productSpecifications.length > 0 && (
					<div className={styles.specs}>
						{productSpecifications?.map((spec) => (
							<div key={spec.label} className={styles.spec}>
								<span className={styles.label}>{spec.label}:</span>
								<span className={styles.value}>{spec.options[0].label}</span>
							</div>
						))}
					</div>
				)}
				<div className={styles.price}>
					<FormattedPrice price={totalPrice} />{" "}
					{salePercentage !== undefined && (
						<small>
							<FormattedPrice price={original_price * item.quantity} />
							{` (${salePercentage.toFixed(2)}%)`}
						</small>
					)}
				</div>
			</div>
			<RemoveItemForm lineItem={item} product={product} />
		</div>
	);
};

export default MiniCartItem;
